import type { LabelContentVariant, LabelContext } from './label.model';

// Re-export generic text utilities from the shared text module.
// These were originally defined here but are now shared with Angular controls.
export { renderLatex, convertLineBreaks } from '../../../../text/rich-text';

/**
 * Default decimal precision for coordinate display.
 */
const COORD_PRECISION = 2;

/**
 * Default decimal precision for degree display.
 */
const DEGREE_PRECISION = 1;

/**
 * Default decimal precision for length display.
 */
const LENGTH_PRECISION = 2;

/**
 * Format a number with fixed decimal places.
 */
function formatNumber(value: number, precision: number): string {
  return value.toFixed(precision);
}

/**
 * Format coordinates as "(x, y)".
 */
function formatCoordinates(x: number, y: number, precision?: number): string {
  const p = precision ?? COORD_PRECISION;
  return `(${formatNumber(x, p)}, ${formatNumber(y, p)})`;
}

/**
 * Format name only.
 */
function formatName(ctx: LabelContext): string {
  return ctx.name;
}

/**
 * Format coordinates only (for points).
 */
function formatCoord(ctx: LabelContext, precision?: number): string {
  if (ctx.type === 'point') {
    return formatCoordinates(ctx.x(), ctx.y(), precision);
  }
  return ctx.name;
}

/**
 * Format name and coordinates: "A (2.00, 3.00)".
 */
function formatNameAndCoord(ctx: LabelContext, precision?: number): string {
  if (ctx.type === 'point') {
    return `${ctx.name} ${formatCoordinates(ctx.x(), ctx.y(), precision)}`;
  }
  return ctx.name;
}

/**
 * Format degrees only (for angles): "45.0°".
 */
function formatDeg(ctx: LabelContext, precision?: number): string {
  if (ctx.type === 'angle') {
    return `${formatNumber(ctx.degrees(), precision ?? DEGREE_PRECISION)}°`;
  }
  return ctx.name;
}

/**
 * Format name and degrees: "∠ABC = 45.0°".
 */
function formatNameAndDegrees(ctx: LabelContext, precision?: number): string {
  if (ctx.type === 'angle') {
    return `∠${ctx.name} = ${formatNumber(ctx.degrees(), precision ?? DEGREE_PRECISION)}°`;
  }
  return ctx.name;
}

/**
 * Format length only (for lines): "5.00".
 */
function formatLength(ctx: LabelContext, precision?: number): string {
  if (ctx.type === 'line') {
    return formatNumber(ctx.length(), precision ?? LENGTH_PRECISION);
  }
  return ctx.name;
}

/**
 * Format name and length (for lines): "Δx = 5.00".
 */
function formatNameAndLength(ctx: LabelContext, precision?: number): string {
  if (ctx.type === 'line') {
    return `${ctx.name} = ${formatNumber(ctx.length(), precision ?? LENGTH_PRECISION)}`;
  }
  return ctx.name;
}

/**
 * Format expression (for curves with a getExpression provider).
 * Returns the LaTeX expression string or falls back to the name.
 */
function formatExpression(ctx: LabelContext): string {
  return ctx.expression?.() ?? ctx.name;
}

/**
 * Registry of label formatters by content variant.
 */
const FORMATTERS: Record<LabelContentVariant, (ctx: LabelContext, precision?: number) => string> = {
  name: formatName,
  coord: formatCoord,
  'name+coord': formatNameAndCoord,
  deg: formatDeg,
  'name+deg': formatNameAndDegrees,
  length: formatLength,
  'name+length': formatNameAndLength,
  expression: formatExpression,
};

/**
 * Get a formatter function for a content variant.
 *
 * @param variant - The content variant
 * @returns Formatter function
 */
export function getFormatter(variant: LabelContentVariant): (ctx: LabelContext, precision?: number) => string {
  return FORMATTERS[variant];
}

/**
 * Check if text contains template placeholders like {name}, {length}, etc.
 */
function hasTemplatePlaceholders(text: string): boolean {
  return /\{[^}]+\}/.test(text);
}

/**
 * Format text using template placeholders.
 * Replaces {placeholder} with values from the context.
 *
 * Available placeholders by element type:
 * - Points: {name}, {x}, {y}
 * - Lines: {name}, {length}
 * - Angles: {name}, {deg}, {rad}
 * - Polygons: {name}, {area}, {perimeter}
 */
function formatTemplate(
  template: string,
  ctx: LabelContext | undefined,
  precision?: number,
  resolveValue?: (path: string) => number | string | undefined,
): string {
  let result = template;

  if (ctx) {
    // Common placeholder
    result = result.replace(/\{name\}/g, ctx.name);

    // Expression placeholder (available on all context types)
    if (ctx.expression) {
      result = result.replace(/\{expression\}/g, ctx.expression());
    }

    // Type-specific placeholders (support per-placeholder precision via {x:N} syntax)
    if (ctx.type === 'point') {
      result = result.replace(/\{x(?::(\d+))?\}/g, (_, p) =>
        formatNumber(ctx.x(), p !== undefined ? parseInt(p, 10) : precision ?? COORD_PRECISION),
      );
      result = result.replace(/\{y(?::(\d+))?\}/g, (_, p) =>
        formatNumber(ctx.y(), p !== undefined ? parseInt(p, 10) : precision ?? COORD_PRECISION),
      );
    } else if (ctx.type === 'line') {
      result = result.replace(/\{length(?::(\d+))?\}/g, (_, p) =>
        formatNumber(ctx.length(), p !== undefined ? parseInt(p, 10) : precision ?? LENGTH_PRECISION),
      );
    } else if (ctx.type === 'angle') {
      result = result.replace(/\{deg(?::(\d+))?\}/g, (_, p) =>
        formatNumber(ctx.degrees(), p !== undefined ? parseInt(p, 10) : precision ?? DEGREE_PRECISION),
      );
      result = result.replace(/\{rad(?::(\d+))?\}/g, (_, p) =>
        formatNumber(ctx.radians(), p !== undefined ? parseInt(p, 10) : precision ?? 3),
      );
    } else if (ctx.type === 'polygon') {
      result = result.replace(/\{area(?::(\d+))?\}/g, (_, p) =>
        formatNumber(ctx.area(), p !== undefined ? parseInt(p, 10) : precision ?? COORD_PRECISION),
      );
      result = result.replace(/\{perimeter(?::(\d+))?\}/g, (_, p) =>
        formatNumber(ctx.perimeter(), p !== undefined ? parseInt(p, 10) : precision ?? COORD_PRECISION),
      );
    }
  }

  // Resolve remaining {dotted.path} placeholders via callback (supports per-placeholder precision)
  if (resolveValue) {
    result = result.replace(/\{([^:}]+)(?::(\d+))?\}/g, (match, path: string, p?: string) => {
      if (!path.includes('.')) {
        return match;
      }
      const value = resolveValue(path);
      if (value === undefined || (typeof value === 'number' && isNaN(value))) {
        return '—';
      }
      if (typeof value === 'string') {
        return value;
      }
      return value.toFixed(p !== undefined ? parseInt(p, 10) : precision ?? 2);
    });
  }

  return result;
}

/**
 * Format label text based on configuration.
 * Priority: text (with template support) > formatFn > contentVariant
 *
 * @param config - Label configuration with text, formatFn, or contentVariant
 * @param ctx - Label context for dynamic formatting
 * @returns Formatted label text
 */
export function formatLabelText(
  config: {
    text?: string;
    formatFn?: (ctx: LabelContext) => string;
    contentVariant: LabelContentVariant;
    precision?: number;
  },
  ctx: LabelContext | undefined,
  resolveValue?: (path: string) => number | string | undefined,
): string {
  // Text has highest priority (supports both static and template)
  if (config.text) {
    if (hasTemplatePlaceholders(config.text)) {
      return formatTemplate(config.text, ctx, config.precision, resolveValue);
    }
    return config.text;
  }

  // Custom format function
  if (config.formatFn && ctx) {
    return config.formatFn(ctx);
  }

  // Use content variant formatter (requires ctx)
  if (ctx) {
    return getFormatter(config.contentVariant)(ctx, config.precision);
  }

  return '';
}
